import { useEffect, useState } from 'react';
import { useAuth } from '@old/hooks/use-auth';
import {
  GridLegacy as Grid,
  TextField,
  Button,
  Link,
  Box,
  IconButton,
  InputAdornment,
  Stack,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router';
import { styledTextField } from '@old/styles/generic-styles';
import { PATH } from '@old/data/core/paths';
import { useGlobalStore } from '@old/global-store/use-store';
import { useEffectOnMount, useToggle } from '@old/hooks/core';
import CircularProgress from '@mui/material/CircularProgress';
import { ToastAction } from '@old/ui/toast/toast-action';
import i18n from '../../../libs/language';
import EyeIcon from '@old/assets/svg/core/eye-hollow-green.svg';
import CrossEyeIcon from '@old/assets/svg/core/crossed-eye-green.svg';
import { useConnection } from '@old/hooks/use-connection';
import GoogleLogo from '@old/assets/svg/integrations-logos/google-logo.svg';
import MicrosoftLogo from '@siloreal:assets:logo/integrations-logos/microsoft-logo.svg';
import { ENDPOINT } from '@old/data/core/endpoints';

interface ILoginRequest {
  username: string;
  password: string;
}

export const LoginForm = () => {
  const { setEmail } = useGlobalStore();
  const [showPassword, setShowPassword] = useToggle(false);
  const { login, isLoading, isError } = useAuth();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const returnUrl = searchParams.get('returnUrl');
  const paramEmail = searchParams.get('email') ?? '';

  const [loginRequest, setLoginRequest] = useState<ILoginRequest>({
    username: paramEmail,
    password: '',
  });

  const submit = async (event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    login({
      username: loginRequest.username,
      password: loginRequest.password,
      fromPath: returnUrl,
    });
  };

  useEffectOnMount(() => {
    setEmail(undefined);
  });

  const handleTogglePasswordVisibility = () => {
    setShowPassword();
  };

  const handleGoogleSignIn = () => {
    let url = `${import.meta.env.VITE_IOF_API}${ENDPOINT.GOOGLE_AUTH}`;
    if (returnUrl) {
      localStorage.setItem('googleAuthReturnUrl', returnUrl);
      url += `?redirectUrl=${encodeURIComponent(returnUrl)}`;
    }
    window.location.href = url;
  };

  const handleMicrosoftSignIn = () => {
    let url = `${import.meta.env.VITE_IOF_API}${ENDPOINT.MICROSOFT_AUTH}`;
    if (returnUrl) {
      localStorage.setItem('googleAuthReturnUrl', returnUrl);
      url += `?redirectUrl=${encodeURIComponent(returnUrl)}`;
    }
    window.location.href = url;
  };

  const isOnline = useConnection();

  useEffect(() => {
    const currentEmail = useGlobalStore.getState().email;
    if (!isOnline) {
      navigate(PATH.NO_CONNECTION);
    } else if (isOnline && currentEmail) {
      navigate(PATH.DASHBOARD);
    }
    // Si el usuario no está logueado y está en /login, no hacer nada extra
    // para no pisar query params como ?email=...
  }, [isOnline]);

  return (
    <Box>
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        direction="column"
        spacing={3}
        component="form"
      >
        <Grid item xs={12}>
          <h1 style={{ fontWeight: '300', margin: 0 }}>{i18n.t('app.public_pages.login')}</h1>
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="email"
            name="username"
            label={i18n.t('app.public_pages.user')}
            variant="outlined"
            size="small"
            defaultValue={paramEmail}
            slotProps={{ htmlInput: { 'data-testid': 'form-field-#email' } }}
            onChange={(e) => {
              setLoginRequest((prev) => ({ ...prev, username: e.target.value.trim() }));
            }}
            sx={styledTextField}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id="password"
            type={showPassword ? 'text' : 'password'}
            name="password"
            label={i18n.t('app.public_pages.password')}
            variant="outlined"
            size="small"
            sx={styledTextField}
            onChange={(e) => {
              setLoginRequest((prev) => ({ ...prev, password: e.target.value }));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                submit(e);
              }
            }}
            slotProps={{
              htmlInput: { 'data-testid': 'form-field-#password' },
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <img src={EyeIcon} /> : <img src={CrossEyeIcon} />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Link
            variant="body2"
            data-testid="link-forgot-password"
            onClick={() => {
              navigate(PATH.FORGOT_PASSWORD);
            }}
            underline="none"
            style={{ cursor: 'pointer' }}
          >
            {i18n.t('app.public_pages.forgot_password')}
          </Link>
        </Grid>
        <Grid item xs={12}>
          <Stack direction="column" spacing={1} alignItems="center">
            <Button
              variant="contained"
              sx={{ width: '400px', color: 'white' }}
              disableElevation
              data-testid="form-action-#submit"
              onClick={submit}
              type="submit"
            >
              {isLoading ? (
                <CircularProgress sx={{ marginLeft: '10px', color: 'white' }} size={24} />
              ) : (
                i18n.t('app.public_pages.ingress')
              )}
            </Button>
            <Button
              variant="outlined"
              sx={{ width: '400px' }}
              disableElevation
              onClick={handleGoogleSignIn}
              startIcon={<img src={GoogleLogo} style={{ width: 18, height: 18 }} />}
            >
              {i18n.t('app.public_pages.signin_google')}
            </Button>
            <Button
              variant="outlined"
              sx={{ width: '400px' }}
              disableElevation
              onClick={handleMicrosoftSignIn}
              startIcon={<img src={MicrosoftLogo} style={{ width: 18, height: 18 }} />}
            >
              {i18n.t('app.public_pages.signin_microsoft')}
            </Button>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Link
            variant="body2"
            data-testid="link-register"
            onClick={() => {
              navigate(PATH.REGISTER);
            }}
            underline="none"
            style={{ cursor: 'pointer' }}
          >
            {i18n.t('app.public_pages.register_question')}
          </Link>
        </Grid>
      </Grid>
      {isError && <ToastAction message={i18n.t('app.public_pages.login_fail')} severity="error" />}
    </Box>
  );
};
